// prune-legacy-ai-orchestrator-leftovers.ts
//
// Vì sao cần module này:
// Bản CLI cũ, nhánh provider `subscription`, ghi `"model": "opus"` vào
// `.claude/settings.json` của workspace (SUBSCRIPTION_DEFAULT_MODEL, đã gỡ).
// Project settings ĐÈ user settings ⇒ key đó âm thầm vô hiệu hoá lựa chọn model
// global của member ở `~/.claude/settings.json` — họ chọn model A, mọi phiên mở
// trong workspace lại chạy model B, không có tín hiệu nào báo.
// Gỡ đường GHI chỉ chữa được workspace MỚI: merge lúc sync chỉ THÊM field pack có
// mà user chưa có (settings-field-mergers.ts), không bao giờ bỏ field đã nằm sẵn
// ⇒ máy member vẫn giữ pin cũ vĩnh viễn. Module này self-heal file đã tồn tại;
// caller là `avatar sync`.
//
// Cùng lượt, dọn nốt state file của tool `ai-orchestrator` (đã gỡ khỏi pack) —
// không còn code path nào đọc chúng.
//
// BẤT BIẾN AN TOÀN — xoá theo GIÁ TRỊ, KHÔNG theo tên key:
// chỉ xoá khi `model` là chuỗi BẰNG NGUYÊN VĂN "opus" (đúng literal bản cũ trồng)
// VÀ workspace KHÔNG phải llmlite/anthropic. Không match bộ phận, không regex.
// Lý do: member có quyền tự pin model cho project ("sonnet", "claude-fable-5[1m]",
// "claude-opus-5"…) — sai phạm vi là giẫm lên lựa chọn cố ý của người ta.
//
// ⚠ Nhận diện subscription bằng VẮNG MẶT `avatarProvider`, không bằng
// `avatarProvider === "subscription"`: chỉ applyLLMLite/applyAnthropic mới ghi
// field đó (write-claude-settings-json-per-project.ts:144,160); nhánh subscription
// KHÔNG ghi. So khớp bằng "subscription" ⇒ điều kiện không bao giờ đúng.
//
// Fail-soft: không throw ở bất kỳ nhánh nào. Settings vắng/hỏng JSON đều trả status
// cho caller log. Đường ghi (backup → atomic write) CHỈ chạy khi khớp chính xác.
//
// Purity: module CHỈ prune. Log/report là việc của caller — cùng phân vai với
// pruneOrphanToolHooks và pruneDeadAvatarModeEnvPlaceholder.

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import type { PackSettingsTemplate } from "./merge-settings-types.js";
import { backupFilename } from "./settings-merge-primitives.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

// Literal DUY NHẤT bản CLI cũ từng ghi ở nhánh subscription.
export const LEGACY_SUBSCRIPTION_MODEL_PIN = "opus";

// State file của tool đã gỡ. Đường dẫn tương đối workspace root.
export const LEGACY_ORCHESTRATOR_STATE_FILES = [
  ".claude/state/ai-orchestrator-model-map.json",
  ".claude/state/ai-orchestrator-route-log.jsonl",
] as const;

export type PruneLegacyStatus = "pruned" | "no-op" | "settings-unreadable";

export interface PruneLegacyResult {
  status: PruneLegacyStatus;
  removedModelPin: boolean;
  // BOOLEAN, không mang giá trị lạ ra ngoài: result đi thẳng vào log của sync, mà
  // `model` của member có thể là chuỗi họ tự đặt.
  keptForeignModel?: boolean;
  removedStateFiles: string[];
  changes: string[];
  backupPath?: string;
  settingsMissing?: boolean;
  warning?: string;
}

function projectSettingsPath(workspacePath: string): string {
  return join(workspacePath, ".claude", "settings.json");
}

// Xoá state file mồ côi. Fail-soft từng file — 1 file lỗi quyền không chặn file kia.
async function removeLegacyStateFiles(workspacePath: string): Promise<string[]> {
  const removed: string[] = [];
  for (const rel of LEGACY_ORCHESTRATOR_STATE_FILES) {
    const abs = join(workspacePath, rel);
    try {
      if (await pathExists(abs)) {
        await fs.unlink(abs);
        removed.push(rel);
      }
    } catch {
      // Không throw, không log ở đây (purity) — file inert, bỏ sót vô hại.
    }
  }
  return removed;
}

export async function pruneLegacyAiOrchestratorLeftovers(
  workspacePath: string,
): Promise<PruneLegacyResult> {
  const removedStateFiles = await removeLegacyStateFiles(workspacePath);
  const settingsPath = projectSettingsPath(workspacePath);

  if (!(await pathExists(settingsPath))) {
    return {
      status: "no-op",
      removedModelPin: false,
      removedStateFiles,
      changes: stateFileChanges(removedStateFiles),
      settingsMissing: true,
    };
  }

  // Read + write trong CÙNG bracket lock (#164): module chạy ở đuôi `avatar sync`,
  // cạnh các prune khác — không lock thì snapshot của nó ghi đè thứ vừa được đặt vào.
  return await withSettingsFileLock(workspacePath, () =>
    pruneUnderLock(settingsPath, removedStateFiles),
  );
}

function stateFileChanges(removedStateFiles: string[]): string[] {
  return removedStateFiles.map((f) => `removed state file ${f} (tool ai-orchestrator đã gỡ)`);
}

async function pruneUnderLock(
  settingsPath: string,
  removedStateFiles: string[],
): Promise<PruneLegacyResult> {
  const baseChanges = stateFileChanges(removedStateFiles);
  let settings: PackSettingsTemplate;
  try {
    settings = await readJson<PackSettingsTemplate>(settingsPath);
  } catch (err) {
    // Hỏng JSON → KHÔNG throw, KHÔNG ghi, KHÔNG backup. Sync sau thử lại.
    return {
      status: "settings-unreadable",
      removedModelPin: false,
      removedStateFiles,
      changes: baseChanges,
      warning: `settings.json không parse được: ${err instanceof Error ? err.message : String(err)} — sync sau sẽ thử lại`,
    };
  }

  const raw = settings as Record<string, unknown>;
  const model = raw.model;
  if (model === undefined) {
    return noOp(removedStateFiles, baseChanges);
  }

  // Provider có ghi `avatarProvider` (llmlite/anthropic) ⇒ `model` là model id THẬT
  // của gateway, API bắt buộc phải có. Giữ nguyên, không đụng.
  if (typeof raw.avatarProvider === "string" && raw.avatarProvider !== "") {
    return { ...noOp(removedStateFiles, baseChanges), keptForeignModel: true };
  }

  // ⚠ GIỮ guard kiểu chuỗi — đừng tin khai báo type: readJson trả thẳng dữ liệu
  // JSON nên `model` ngoài đời có thể là number/object.
  if (typeof model !== "string" || model !== LEGACY_SUBSCRIPTION_MODEL_PIN) {
    return { ...noOp(removedStateFiles, baseChanges), keptForeignModel: true };
  }

  // Bỏ key bằng rest-destructuring — convention codebase. Spread bảo toàn thứ tự
  // các key còn lại.
  const { model: _legacyPin, ...restSettings } = raw;

  const backupPath = backupFilename(settingsPath);
  await fs.copyFile(settingsPath, backupPath);
  // Giữ mode 0600 — file chứa API key của member.
  await writeJsonAtomic(settingsPath, restSettings, SECRET_FILE_MODE);

  return {
    status: "pruned",
    removedModelPin: true,
    removedStateFiles,
    changes: [
      ...baseChanges,
      `removed legacy model pin "${LEGACY_SUBSCRIPTION_MODEL_PIN}" (bản CLI cũ ghi ở nhánh subscription; project settings đè lựa chọn model global của bạn)`,
    ],
    backupPath,
  };
}

function noOp(removedStateFiles: string[], changes: string[]): PruneLegacyResult {
  return {
    status: removedStateFiles.length > 0 ? "pruned" : "no-op",
    removedModelPin: false,
    removedStateFiles,
    changes,
  };
}
